import React, { ReactElement } from 'react';
import { MB_Button } from '@/components/general/button';
import { MB_Pressable, MB_PressableStateCallbackType } from '@/components/general/pressable';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import { StyleProp, ViewStyle, StyleSheet, TextStyle } from 'react-native';

// TODO: Add customization options.

export interface PageIndicatorButtonProps {
  /**
   * Title to be shown inside the button.
   */
  title: string;

  /**
   * If true, indicate that the button is active. This means
   * that the button is selected.
   */
  active?: boolean;

  /**
   * Callback called when the button is pressed.
   */
  onPress?: () => void;

  /**
   * General component style.
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the button.
   */
  buttonStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the text inside the button.
   */
  textStyle?: StyleProp<TextStyle>;

  /**
   * If true, the button will be disabled.
   */
  disabled?: boolean;

  /**
   * Left element to be shown inside the button.
   */
  icon?: ReactElement;

  /**
   * Right element to be shown inside the button.
   */
  rightIcon?: ReactElement;

  /**
   * Color of the button when it is available to be selected.
   */
  activeColor?: string;

  /**
   * Color of the button when it is active, i.e. selected.
   */
  selectColor?: string;

  /**
   * Color of the button when it is hovered.
   */
  hoverColor?: string;

  /**
   * Color of the text when the button is active.
   */
  activeTextColor?: string;

  /**
   * Color of the text when the button is inactive.
   */
  inactiveTextColor?: string;
}

const PageIndicatorButton = ({
  title,
  active,
  onPress,
  containerStyle,
  buttonStyle,
  disabled,
  icon,
  rightIcon,
  textStyle,
  activeColor = '#5D3E65',
  selectColor = '#FFFFFF',
  hoverColor = '#4D2D52',
  activeTextColor = '#5D3E65',
  inactiveTextColor = '#FFFFFF',
}: PageIndicatorButtonProps) => (
  <MB_Pressable pointerEvents={onPress ? undefined : 'none'} style={containerStyle}>
    {({ hovered }: MB_PressableStateCallbackType) => {
      const getBackgroundColor = () => {
        if (active) {
          return selectColor;
        }
        if (hovered && !disabled && onPress) {
          return hoverColor;
        }
        return activeColor;
      };

      return (
        <MB_Button
          title={title}
          style={[styles.button, buttonStyle, { backgroundColor: getBackgroundColor() }]}
          textStyle={[textStyle, { color: active ? activeTextColor : inactiveTextColor }]}
          onPress={onPress}
          leftElement={icon}
          rightElement={rightIcon}
          disabled={disabled}
          leftElementStyle={{ marginEnd: 0 }}
        />
      );
    }}
  </MB_Pressable>
);

export { PageIndicatorButton };

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
  },
  button: {
    height: 41.16,
    width: 41.16,
    borderRadius: 9.73,
    borderWidth: 1,
    borderColor: mbApplyTransparency('#BAC0CA', 0.3),
  },
});
